<script lang="ts">
  /**
   * Сучасний "bubble" для linked record (Airtable/Notion-стиль): avatar з
   * ініціалів + title (+ subtitle коли не dense) + hover-card з деталями.
   * Використовується у display (read-only) та як selected-chip у picker'і.
   */
  import Icon from '../../components/Icon.svelte';
  import type { RelationCard } from '../data-tables-api';
  import RelationHoverCard, { avatarBg } from './RelationHoverCard.svelte';

  let {
    card,
    slug,
    id,
    dense = false,
    removable = false,
    disabled = false,
    onRemove,
  }: {
    /** Резолвлена картка; null = ще вантажиться (skeleton). */
    card: RelationCard | null;
    slug: string;
    /** id обов'язковий навіть коли card ще null (для href/skeleton). */
    id: string;
    dense?: boolean;
    removable?: boolean;
    disabled?: boolean;
    onRemove?: () => void;
  } = $props();

  function hrefFor(): string | null {
    if (!slug) return null;
    if (slug === 'users') return `#/persons/${id}`;
    if (slug === 'payments') return `#/payments/${id}`;
    return `#/tables/${slug}/${id}`;
  }
  const link = $derived(hrefFor());
  const title = $derived(card?.title ?? `${id.slice(0, 8)}…`);
  const isTombstone = $derived(!!card?.deleted);

  let el = $state<HTMLElement | null>(null);
  let rect = $state<DOMRect | null>(null);
  let hovering = $state(false);
  let timer: ReturnType<typeof setTimeout> | undefined;

  function openHover() {
    if (!card || isTombstone) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (el) {
        rect = el.getBoundingClientRect();
        hovering = true;
      }
    }, 280);
  }
  function closeHover() {
    clearTimeout(timer);
    hovering = false;
  }
</script>

<span
  bind:this={el}
  class="group/bubble inline-flex max-w-full items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-1.5 transition-colors
    {isTombstone
      ? 'border-dashed border-[var(--color-border-3)] bg-[var(--color-bg-3)] text-[var(--color-text-muted)]'
      : 'border-[var(--color-border-3)] bg-white hover:border-[var(--color-pu-2)]'}"
  onmouseenter={openHover}
  onmouseleave={closeHover}
  onfocusin={openHover}
  onfocusout={closeHover}
  role="group"
>
  <a
    href={link ?? undefined}
    class="inline-flex min-w-0 items-center gap-1.5 no-underline text-inherit"
    title={card?.subtitle ? `${title} · ${card.subtitle}` : title}
  >
    <span
      class="grid place-items-center rounded-full text-white font-bold flex-none
        {dense ? 'w-5 h-5 text-[9px]' : 'w-6 h-6 text-[10px]'}"
      style="background: {card ? avatarBg(card) : '#cbd5e1'};"
    >{card?.initials ?? '…'}</span>
    <span class="flex min-w-0 flex-col leading-tight">
      <span class="truncate text-[12px] font-medium {isTombstone ? 'line-through' : ''}">{title}</span>
      {#if !dense && card?.subtitle}
        <span class="truncate text-[10px] text-[var(--color-text-muted)]">{card.subtitle}</span>
      {/if}
    </span>
  </a>

  {#if removable && !disabled}
    <button
      type="button"
      onclick={(e) => { e.stopPropagation(); e.preventDefault(); onRemove?.(); }}
      class="inline-flex flex-none items-center rounded-full p-0.5 text-[var(--color-text-muted-2)] hover:bg-rose-50 hover:text-rose-600"
      title="Прибрати зв'язок"
      aria-label="Прибрати зв'язок"
    ><Icon name="close" size={12} /></button>
  {/if}
</span>

{#if hovering && card && rect && !isTombstone}
  <RelationHoverCard {card} {rect} href={link} />
{/if}
